import useGetUserNotifications from "api/Notifications/useGetUserNotifications";
import useMarkAllNotificationsAsRead from "api/Notifications/useMarkAllNotificationsAsRead";
import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import "./notifications.css";
import { FaRegBell } from "react-icons/fa";
import { FiX } from "react-icons/fi";
import { useHistory, useLocation } from "react-router-dom";
import NotificationItem from "./NotificationItem";

let nextNotificationId = 0;

/**
 * @param {Object} props
 * @param {boolean} props.isAuthenticated
 * @returns {JSX.Element}
 */
const NotificationBell = (props) => {
	const { isAuthenticated = false } = props;
	const [show, setShow] = useState(false);
	const history = useHistory();

	const dropdownRef = useRef(null), panelRef = useRef(null), triggerRef = useRef(null);
    const [panelId] = useState(() => `notifications-${++nextNotificationId}`);
    const [position, setPosition] = useState({ visibility: 'hidden' });
    const location = useLocation();
    const close = () => { setShow(false); triggerRef.current?.focus({ preventScroll: true }); };
    useEffect(() => { setShow(false); }, [location.pathname, location.search]);
    useLayoutEffect(() => {
        if (!show) return;
        const place = () => {
            const bounds = triggerRef.current?.getBoundingClientRect();
            if (!bounds) return;
            const width = Math.min(352, window.innerWidth - 24);
            setPosition({ width, left: Math.max(12, Math.min(bounds.right - width, window.innerWidth - width - 12)), top: Math.min(bounds.bottom + 8, window.innerHeight - 100), maxHeight: Math.max(80, window.innerHeight - bounds.bottom - 20) });
        };
        place();
        panelRef.current?.querySelector('button')?.focus({ preventScroll: true });
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
    }, [show]);

	// Close dropdown when clicking outside
	useEffect(() => {
		if (!show) return;
		const handleClickOutside = (event) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target) && !panelRef.current?.contains(event.target)) {
				setShow(false);
			}
		};
		const escape = (event) => { if (event.key === 'Escape') { event.preventDefault(); setShow(false); triggerRef.current?.focus(); } };
        document.addEventListener('keydown', escape);
        document.addEventListener("pointerdown", handleClickOutside);
		return () => {
			document.removeEventListener("pointerdown", handleClickOutside);
            document.removeEventListener("keydown", escape);
		};
	}, [show]);

	const handleViewAll = () => {
		setShow(false);
		history.push("/notifications/all");
	};

	const { data: notifications, isLoading } = useGetUserNotifications({
		recent: true,
		status: "unread",
	});

	const { mutateAsync: markAllAsRead, isLoading: isMarkingAll } = useMarkAllNotificationsAsRead();

	const unreadNotifications = notifications?.data?.records || [];
	const unreadCount = Number(notifications?.data?.unread_count ?? unreadNotifications.length);

	if (!isAuthenticated) return null;

	return (
		<div className="relative inline-block" ref={dropdownRef}>
			<button
				type="button"
                ref={triggerRef}
                aria-expanded={show}
                aria-controls={show ? panelId : undefined}
                className="notification-trigger relative p-2 rounded-full hover:bg-gray-100"
				onClick={() => setShow((s) => !s)}
				aria-label="Notifications"
			>
				<FaRegBell size={24} />
				{unreadCount > 0 &&
					(isLoading ? (
						<div className="w-4 h-4 border-t-2 border-b-2 border-gray-900 rounded-full animate-spin absolute -top-1 -right-1" />
					) : (
						<span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
							{unreadCount}
						</span>
					))}
			</button>

			{show && createPortal(
				<div ref={panelRef} id={panelId} role="region" aria-label="My notifications" style={position} className="notification-panel bg-white border border-gray-200 rounded-md shadow-lg" onKeyDown={(event) => {
                    if (event.key !== 'Tab') return;
                    const controls = Array.from(panelRef.current.querySelectorAll('button:not([disabled]), a[href]'));
                    if ((event.shiftKey && document.activeElement === controls[0]) || (!event.shiftKey && document.activeElement === controls[controls.length - 1])) close();
                }}>
					<div className="flex justify-between items-center px-4 py-2 font-bold border-b border-gray-200">
						<div>
							<p className="text-base">My notifications</p>
							<p className="text-xs text-gray-500 font-normal">{unreadCount} unread</p>
						</div>

						<button
							type="button"
                            className="p-1 rounded-full hover:bg-gray-200"
							onClick={close}
							aria-label="Close notifications dropdown"
						>
							<FiX size={20} />
						</button>
					</div>

					<div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center">
						<button
							type="button"
							onClick={() => markAllAsRead()}
							disabled={unreadCount === 0 || isMarkingAll}
							className="text-sm text-akgreen hover:underline disabled:text-gray-400 disabled:no-underline"
						>
							Mark all as read
						</button>
						<button
							type="button"
							className="text-blue-600 hover:underline text-sm font-medium"
							onClick={handleViewAll}
						>
							View all
						</button>
					</div>

					<div className="notification-list">
						{unreadNotifications.length === 0 ? (
							<div className="px-4 py-3 text-gray-500 text-sm">No unread notifications</div>
						) : (
							unreadNotifications.map((notif) => (
								<NotificationItem key={notif.id} {...notif} onClose={() => setShow(false)} />
							))
						)}
					</div>
				</div>, document.body
			)}
		</div>
	);
};

export default NotificationBell;
